<template>
  <v-card elevation="1" class="container-card">
    <v-row align="center" justify="start" class="top-bar pa-3">
      <v-col cols="12">
        <div class="d-flex justify-start align-center">
          <v-icon class="mr-2" icon="person" />
          <div class="md-light">Shopper Details</div>
          <v-spacer />
          <v-progress-circular
            v-if="loading"
            :indeterminate="true"
            size="25"
            width="2"
            class="loading-wheel"
          />
        </div>
      </v-col>
    </v-row>
    <v-col cols="12">
      <v-divider />
    </v-col>
    <div class="d-flex align-center justify-start">
      <v-form v-model="checkoutFieldsValid" class="w-100">
        <v-row align="center" justify="start" class="pa-3">
          <v-col
            cols="12"
            sm="6"
            v-for="field in checkoutFieldsToDisplay"
            :key="field"
          >
            <v-text-field
              @update:modelValue="madeChanges(field)"
              :disabled="getDisabled(field)"
              class="pr-3"
              :value="checkoutFieldsValues[field]"
              v-model="checkoutFieldsValues[field]"
              :label="field"
              :rules="getRules(field)"
              data-testid="checkout-field-text-field"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            v-for="(field, i) in checkoutFieldDropdowns"
            :key="field + i + '_checkoutFieldDropdowns'"
            data-testid="checkout-configured-select-cell"
          >
            <v-select
              @update:modelValue="madeChanges"
              class="pr-3"
              :label="field['label']"
              v-model="checkoutFieldsValues[field['label']]"
              :value="checkoutFieldsValues[field['label']]"
              :items="field['items']"
              :rules="getRules(field)"
              :disabled="disabled"
              :data-testid="`co-${slugifyFieldName(field['label'])}`"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            v-for="(field, i) in checkoutCustomDropdowns"
            :key="field + i + '_checkoutCustomDropdowns'"
            data-testid="checkout-custom-select-cell"
          >
            <v-select
              @update:modelValue="madeChanges"
              class="pr-3"
              :label="field['name']"
              v-model="checkoutFieldsValues[field['name']]"
              :value="checkoutFieldsValues[field['name']]"
              :items="field['options']"
              :rules="getRules(field)"
              :disabled="disabled"
              :data-testid="`co-custom-${slugifyFieldName(field['name'])}`"
            />
          </v-col>
          <!--
            The typed department picker, the storefront's (CartCheckout
            `co-department`): shown only when the store maps to 2+ client
            departments, where checkout REQUIRES the pick. 0 -> no prompt;
            1 -> attributed server-side. The id goes up to AddOrder, which
            posts it as `client_department_id`.
          -->
          <v-col
            v-if="departmentPickerMode === 'required'"
            cols="12"
            sm="6"
          >
            <v-select
              class="pr-3"
              label="Department"
              :model-value="clientDepartmentId"
              @update:modelValue="$emit('update:clientDepartmentId', $event)"
              :items="clientDepartments"
              :item-title="(d) => d.name"
              :item-value="(d) => d.id"
              :rules="[validateNotEmpty]"
              :disabled="disabled"
              data-testid="add-order-department"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              clearable
              label="Coupon"
              class="pr-3"
              v-if="campaign.coupons_enabled"
              :items="campaign.coupons"
              item-title="name"
              v-model="couponCode"
              @update:modelValue="changeCoupon"
              data-testid="checkout-field-coupon"
              :disabled="disabled"
            />
          </v-col>
        </v-row>
        <!--
          Where the order ships follows the store's shipping mode, as on the
          storefront (#540, Jake 2026-10-26). A company / department store
          ships where the SERVER resolves (`checkout_quote.ship_to`, priced
          by the cart); nobody types it. A shopper-address store always
          takes the address below. The Location dropdown is only a label on
          the order (#548): it never changes where the order ships.
        -->
        <v-row
          v-if="shipsToStoreDestination"
          class="pa-3"
          data-testid="add-order-ship-to"
        >
          <v-col cols="12">
            <cart-ship-to-destination
              :ship-to="getShopperShipTo"
              :state="getShopperShipToState"
            />
          </v-col>
        </v-row>

        <!-- Shopper Location Form -->

        <v-form
          ref="form"
          v-model="shopperLocationFormValid"
          v-if="displayShopperLocation"
        >
          <v-divider />
          <v-col cols="12" class="pa-3">
            <div class="d-flex justify-start align-center">
              <v-icon class="mr-2" icon="place" />
              <div class="md-light">Shopper Location</div>
              <v-spacer />
              <v-progress-circular
                v-if="shopperLocationLoading"
                :indeterminate="true"
                size="25"
                width="2"
                class="loading-wheel"
              />
            </div>
          </v-col>
          <v-divider />

          <v-row class="pa-3">
            <v-col cols="12" sm="6">
              <v-text-field
                @keyup="madeChanges('Shopper Location')"
                label="Street and number *"
                v-model="shopperLocation.street_and_number"
                :rules="[validateNotEmpty]"
                :disabled="shopperLocationLoading || disabled"
                data-testid="ct-locations-street"
                class="pr-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                @keyup="madeChanges('Shopper Location')"
                label="City *"
                v-model="shopperLocation.city"
                :rules="[validateNotEmpty]"
                class="pr-3"
                :disabled="shopperLocationLoading || disabled"
                data-testid="ct-locations-city"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                @keyup="madeChanges('Shopper Location')"
                label="Postal Code *"
                v-model="shopperLocation.zip_code"
                :rules="[validateNotEmpty]"
                class="pr-3"
                :disabled="shopperLocationLoading || disabled"
                data-testid="ct-locations-zip"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                @keyup="madeChanges('Shopper Location')"
                label="Suite, Unit, Building, Floor, etc."
                v-model="shopperLocation.suite_unit_etc"
                class="pr-3"
                :disabled="shopperLocationLoading || disabled"
                data-testid="ct-locations-suite"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete
                @keyup="madeChanges('Shopper Location')"
                hide-no-data
                browser-autocomplete="disabled"
                label="Country *"
                v-model="shopperLocation.country"
                :rules="[validateNotEmpty]"
                data-testid="ct-locations-country"
                item-title="name"
                item-value="code"
                class="pr-3"
                :disabled="shopperLocationLoading || disabled"
                :items="countriesAndCodes"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-if="isUS"
                @update:modelValue="madeChanges('Shopper Location')"
                hide-no-data
                browser-autocomplete="disabled"
                label="State *"
                v-model="shopperLocation.region"
                :rules="[validateNotEmpty]"
                class="pr-3"
                data-testid="ct-locations-state"
                :disabled="shopperLocationLoading || disabled"
                :items="usStates"
                item-title="name"
                item-value="code"
              ></v-autocomplete>
              <v-text-field
                v-else
                @keyup="madeChanges('Shopper Location')"
                browser-autocomplete="disabled"
                label="State/Province *"
                v-model="shopperLocation.region"
                :rules="[validateNotEmpty]"
                class="pr-3"
                data-testid="ct-locations-state-text"
                :disabled="shopperLocationLoading || disabled"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-form>
    </div>
    <validate-address-dialog
      ref="validateAddressDialog"
      :originalLocation="shopperLocation"
      @useValidatedAddress="updateAddressToValidated"
      @useOriginalAddress="keepOriginalAddress"
      @dialogOpened="shopperLocationLoading = false"
      @addressUnparseable="shopperLocationLoading = false"
    />
  </v-card>
</template>

<script>
import store from "@/store";
import rules from "@/mixins/rules";
import helpers from "@/mixins/helpers";
import ValidateAddressDialog from "@/pages/distributor/store-editor/StoreLocations/ValidateAddressDialog.vue";
import CartShipToDestination from "@/components/Storefront/CartShipToDestination.vue";
import { mapGetters } from "vuex";
import {
  departmentPickerMode,
  storeResolvesDestination,
} from "@/utils/campaign";

// The Location dropdown's "no location" choice, the default. The dropdown
// records which of the store's locations the order is for -- a LABEL the
// Drop Shipment report groups orders by. It never changes where the order
// ships (the store's shipping mode decides, #540) or what it is charged: a
// pick-up location pays shipping like any other order (Bug Intake #548,
// Jake 2026-10-26).
export const NO_LOCATION_OPTION = "None";

export default {
  components: { ValidateAddressDialog, CartShipToDestination },
  mixins: [helpers, rules],

  emits: ["addressValidated", "update:clientDepartmentId"],

  props: {
    storeID: {
      type: Number,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    // The typed department pick (v-model:clientDepartmentId); null until
    // picked, and unused unless the store requires one.
    clientDepartmentId: {
      type: Number,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      campaign: {},
      loading: false,
      shopperLocationLoading: false,
      checkoutFields: [],
      checkoutFieldDropdowns: [],
      checkoutFieldsValid: true,
      // Keyed by field label. An OBJECT: the old array (written by string
      // key) was invisible to the watcher below, so the store kept `{}` and
      // orders were placed with no shopper details (#540).
      checkoutFieldsValues: {},
      checkoutCustomDropdowns: [],
      shopperLocation: {
        city: "",
        country: "",
        region: "",
        street_and_number: "",
        suite_unit_etc: "",
        zip_code: "",
      },
      shopperLocationFormValid: false,
      countriesAndCodes: [],

      addressValidated: false,
      // The address the AVS dialog last accepted (JSON), so an edit after
      // it -- typed or picked -- needs validating again.
      validatedAddressKey: null,
      couponCode: null,
    };
  },

  computed: {
    ...mapGetters(["getShopperShipTo", "getShopperShipToState"]),

    // The store's locations the Location dropdown offers (pick-up or not:
    // it is a label, see NO_LOCATION_OPTION).
    storeLocations() {
      return ((this.campaign && this.campaign.locations) || []).filter(
        (location) => location && location.nickname
      );
    },
    // The store, not the order, decides where it ships.
    shipsToStoreDestination() {
      return storeResolvesDestination(this.campaign);
    },
    // The store's typed client-department mapping. The backend emits []
    // when the Departments feature is off, which hides the picker.
    clientDepartments() {
      return (this.campaign && this.campaign.client_departments) || [];
    },
    departmentPickerMode() {
      return departmentPickerMode(this.clientDepartments);
    },
    // The address form: every shopper-address store, whatever Location is
    // chosen. Keyed on the store's shipping mode, not on the legacy
    // "Shopper Location" checkout field (#540).
    displayShopperLocation() {
      return (
        Boolean(this.campaign && this.campaign.id !== undefined) &&
        !storeResolvesDestination(this.campaign)
      );
    },
    //returns the list of checkout fields to display
    //namely whether or not to display the "Shopper Location" field
    checkoutFieldsToDisplay() {
      if (this.checkoutFieldsValues);

      let fields = [];
      this.checkoutFields.forEach((field) => {
        if (field != "Shopper Location") fields.push(field);
      });

      return fields;
    },
    isUS() {
      const c = this.shopperLocation.country;
      return !c || c === "US" || c === "United States";
    },
    usStates() {
      const stateMap = this.$store.getters.getStates;
      return Object.entries(stateMap).map(([name, code]) => ({
        name: `${name} (${code})`,
        code,
      }));
    },
  },

  watch: {
    checkoutFieldsValues: {
      deep: true,
      immediate: true,
      handler() {
        this.$store.commit(
          "setCheckoutFieldsValues",
          this.checkoutFieldsValues
        );
      },
    },

    checkoutFieldsValid: {
      handler() {
        let shopperLocationFormValid = false;
        if (
          !this.displayShopperLocation ||
          (this.displayShopperLocation &&
            this.shopperLocationFormValid &&
            this.addressValidated)
        ) {
          shopperLocationFormValid = true;
        }
        this.$store.commit("setShopperDetailsValid", this.checkoutFieldsValid);
      },
    },

    // Every edit to the typed address (keyboard or a picked country /
    // state) reaches the cart, which prices it again.
    shopperLocation: {
      deep: true,
      handler() {
        if (!this.displayShopperLocation) return;
        if (
          this.addressValidated &&
          JSON.stringify(this.shopperLocation) !== this.validatedAddressKey
        ) {
          this.addressValidated = false;
        }
        this.commitShipTo();
      },
    },

    "shopperLocation.country"(newVal, oldVal) {
      // When the country changes, clear region — otherwise a stale 2-letter US
      // code leaks into the free-text non-US path (or vice versa) and silently
      // breaks ShipEngine address validation.
      if (oldVal && newVal !== oldVal) {
        this.shopperLocation.region = "";
      }
    },

    addressValidated: {
      handler() {
        let shopperLocationFormValid = false;
        if (this.displayShopperLocation && this.addressValidated) {
          shopperLocationFormValid = true;
        } else if (!this.displayShopperLocation) {
          shopperLocationFormValid = true;
        }
        this.$store.commit("setShopperDetailsValid", this.checkoutFieldsValid);
      },
    },
  },

  async mounted() {
    await this.loadCampaign();
    this.commitShipTo();
    await this.getCountries();
  },

  methods: {
    // Builds the stable `data-testid` suffix for a campaign-configured
    // dropdown, so automation can address a specific field (Department,
    // Manager, Location, ...) instead of an index into a v-for.
    //
    // These fields come from campaign config at runtime, so there is no
    // frozen id list a lint extractor could derive the family from the way
    // check_testid_dependencies.py derives ct-<id> from SECTION_IDS. The
    // wrapping v-col therefore carries a LITERAL testid as the stable,
    // statically-greppable handle, and this only refines it per field.
    //
    // Presentation is untouched: the visible label is still field['label'].
    slugifyFieldName(name) {
      return String(name ?? "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    },
    clearShopperDetails() {
      this.checkoutFieldsValues = this.offersLocation()
        ? { Location: NO_LOCATION_OPTION }
        : {};
      this.$emit("update:clientDepartmentId", null);
    },
    validateAddress() {
      if (this.displayShopperLocation) {
        this.shopperLocationLoading = true;
        this.$refs.validateAddressDialog.show();
      }
    },
    updateAddressToValidated(location) {
      this.shopperLocation = { ...location };
      this.acceptAddress();
    },
    keepOriginalAddress() {
      this.acceptAddress();
    },
    acceptAddress() {
      this.validatedAddressKey = JSON.stringify(this.shopperLocation);
      this.addressValidated = true;
      this.commitShipTo();
      this.$emit("addressValidated");
    },
    changeCoupon(coupon) {
      this.$store.commit("setShopperCoupon", coupon);
    },
    async getCountries() {
      let result;
      try {
        result = await this.$axios.get(`/campaign/getCountryCodes`);
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      } finally {
        this.countriesAndCodes = result.data;
      }
    },
    //populates v-select fields for checkout fields that require selection rather than text entry
    populateCheckoutFieldDropdowns() {
      //if campaign contains these fields, generate info for v-selects
      if (this.campaign.managers) {
        let items = [];
        this.campaign.managers.forEach((manager) => {
          items.push(manager.first_name + " " + manager.last_name);
        });
        this.generateCheckoutFieldDropdown("Manager", items);
      }
      // A legacy "Department" checkout field is never rendered: checkout
      // drops checkout_fields["Department"], so a control writing it would
      // do nothing (and with no departments mapped it was a required
      // dropdown with no items). The department is the typed picker
      // (departmentPickerMode), as on the storefront.
      const departmentIndex = this.checkoutFields.indexOf("Department");
      if (departmentIndex !== -1) {
        this.checkoutFields.splice(departmentIndex, 1);
      }
      // The Location label (NO_LOCATION_OPTION): the store's locations,
      // none chosen by default. A store that asks for Location but has no
      // location rows has nothing to choose, so the field is not shown.
      if (this.offersLocation()) {
        this.generateCheckoutFieldDropdown("Location", [
          NO_LOCATION_OPTION,
          ...this.storeLocations.map((location) => location.nickname),
        ]);
        this.checkoutFieldsValues.Location = NO_LOCATION_OPTION;
      } else {
        const locationIndex = this.checkoutFields.indexOf("Location");
        if (locationIndex !== -1) {
          this.checkoutFields.splice(locationIndex, 1);
        }
      }
    },

    // The store asks for Location and has locations to offer. Read from the
    // store, not `checkoutFields` (the dropdown is spliced out of that).
    offersLocation() {
      const fields = (this.campaign && this.campaign.checkout_fields) || [];
      return fields.includes("Location") && this.storeLocations.length > 0;
    },

    //generates the checkoutfield dropdown information for an inputed checkout field
    generateCheckoutFieldDropdown(label, items) {
      //remove field from checkoutfields[]
      //and populate checkoutFieldDropdowns[]
      let index = this.checkoutFields.indexOf(label);
      if (index == -1) return;
      this.checkoutFields.splice(index, 1);
      this.checkoutFieldDropdowns.push({
        label: label,
        items: items,
      });
    },

    async loadCampaign() {
      this.loading = true;
      let result;
      try {
        result = await this.$axios.get(
          `/campaign/getCampaignById/${this.storeID}`
        );
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      } finally {
        this.campaign = result.data;
        this.checkoutFields = [...(result.data.checkout_fields || [])];
        this.checkoutCustomDropdowns = result.data.dropdown_fields;
        this.populateCheckoutFieldDropdowns();
        this.loading = false;
      }
    },

    async setShopperAllowance() {
      // Slice 2: source the shopper's remaining allowance from the v2 endpoint
      // (allowance_policies / allowance_grants), NOT the legacy flat
      // stores.company_allowance column. The endpoint returns 0 for
      // non-allowance stores, so we no longer gate on a legacy column read.
      // account_id names the shopper being acted on -- the admin is a
      // shopper.act_as caller, so g.account is the admin, not the shopper.
      try {
        const shopperAccountId =
          this.$store.state.account && this.$store.state.account._jv
            ? this.$store.state.account._jv.id
            : undefined;
        let response = await this.$axios.post(
          "/order/checkRemainedAllowance",
          {
            campaign_id: this.campaign._jv.id,
            account_id: shopperAccountId,
            // On an email-domain store the allowance belongs to an email
            // address, not an account (card #596 follow-up): name it.
            company_email:
              (this.checkoutFieldsValues || {})["Company Email"] || null,
          }
        );
        this.$store.commit(
          "setShopperAllowance",
          response.data.remained_allowance
        );
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      }
    },

    madeChanges(field) {
      if (field == "Shopper Location") {
        this.addressValidated = false;
      }

      if (
        field == "Company Email" &&
        this.campaign.is_prevent_order_duplication
      ) {
        this.setShopperAllowance();
      }

      this.commitShipTo();
    },

    // Tell the cart where the order ships, so it can price it (#540). The
    // Location label plays no part (#548):
    //  - a company / department store: nothing typed (the cart asks the
    //    server for the destination);
    //  - a shopper-address store: the typed address, as typed. The cart
    //    prices it once it is complete; Place Order validates it, and a
    //    corrected address is priced again.
    commitShipTo() {
      if (storeResolvesDestination(this.campaign)) {
        this.addressValidated = true;
        this.$store.commit("setShopperLocation", {});
        this.$store.commit("setIsCompanyLocation", true);
      } else {
        this.$store.commit("setShopperLocation", { ...this.shopperLocation });
        this.$store.commit("setIsCompanyLocation", false);
      }
      this.$store.commit("setShopperLocationValidated", this.addressValidated);
    },

    getDisabled(field) {
      if (this.disabled) {
        return true;
      }
      return false;
    },

    //returns the rule for the respective checkout field form
    getRules(field) {
      if (field == "Shopper Location") {
        if (this.getDisabled(field)) {
          return [];
        } else {
          return [this.validateNotEmpty, this.validateNotSpaces];
        }
      } else if (field == "Company Email") {
        return [
          this.validateNotEmpty,
          this.validateNotSpaces,
          this.validateEmail,
          this.validateEmailDomain,
        ];
      } else {
        return [this.validateNotEmpty, this.validateNotSpaces];
      }
    },

    validateEmailDomain(email) {
      if (
        this.campaign.is_restrict_email_domain &&
        this.campaign.email_domains &&
        this.campaign.email_domains.length
      ) {
        for (let i = 0; i < this.campaign.email_domains.length; i++) {
          if (
            this.validateSpecificEmailDomain(
              email,
              this.campaign.email_domains[i]
            ) === true
          ) {
            return true;
          }
        }
        return "Domain format is incorrect";
      } else {
        return true;
      }
    },
  },
};
</script>

<style scoped>
.container-card {
  width: 100%;
  height: 100%;
  border-radius: 5px;
}

.top-bar {
  background: white;
}
</style>

